import Button from '@jetbrains/ring-ui/components/button/button'
import Checkbox from '@jetbrains/ring-ui/components/checkbox/checkbox'
import LoaderInline from '@jetbrains/ring-ui/components/loader-inline/loader-inline'
import Panel from '@jetbrains/ring-ui/components/panel/panel'
import plur from 'plur'
import * as React from 'react'
import type {PreloadedQuery, UseQueryLoaderLoadQueryOptions} from 'react-relay'
import {usePreloadedQuery, useQueryLoader} from 'react-relay'

import type {AgentPoolId} from '../../../../../../../types'
import {stringifyId} from '../../../../../../../types'
import {emptyObject} from '../../../../../../../utils/empty'
import {subscribeOnOverallEvents} from '../../../../../../../utils/subscriber'
import {OVERALL_AGENT_POOL_EVENT_TOPICS} from '../../../../AgentsPages.actions'
import {AgentTreeItemType} from '../../../../AgentsSidebar/AgentsSidebar.types'
import LinearAgentType from '../../../../components/LinearAgentType/LinearAgentType'
import MatchingTitle from '../../../../components/MatchingItem/MatchingItem'
import Search from '../../../../components/Search/Search'
import {useTreeState} from '../../../AgentPoolPage.hooks'
import {stopPropagation} from '../../../AgentPoolPage.utils'

import type {
  AssignAgentsDialogAssignablePoolItemsQuery,
  AssignAgentsDialogAssignablePoolItemsQuery$data,
} from './__generated__/AssignAgentsDialogAssignablePoolItemsQuery.graphql'
import {AgentsSelectionSummary} from './AgentsSelectionSummary/AgentsSelectionSummary'
import {AgentItem} from './AgentsTreeItem/AgentsTreeItem'
import AssignAgentsButton from './AssignAgentsButton/AssignAgentsButton'
import {useSelectionState} from './AssignAgentsDialog.hooks'
import {assignableAgentPoolItemsQuery} from './AssignAgentsDialog.queries'
import {
  buildAssignableAgentsTree,
  getAgentsSelectionSummary,
  matchAssignableAgentsTree,
} from './AssignAgentsDialog.selectors'
import type {AssignableAgentsTree} from './AssignAgentsDialog.types'
import {onOverflowingElementMouseEnter} from './AssignAgentsDialog.utils'
import ExpandableGroup from './ExpandableGroup/ExpandableGroup'

import styles from './AssignAgentsDialog.css'

type DialogProps = {
  agentPoolId: AgentPoolId
  onCloseAttempt?: () => void
  agentsNumber?: number | null
  maxAgentsNumber?: number | null
}

type ContentProps = DialogProps & {
  queryRef: PreloadedQuery<AssignAgentsDialogAssignablePoolItemsQuery>
}

const loader = <LoaderInline className={styles.loader} />
const SEARCH_INPUT_ID = 'ASSIGN_AGENTS_DIALOG_SEARCH'

const assignableAgentPoolItemsQueryFetchOptions: UseQueryLoaderLoadQueryOptions = {
  fetchPolicy: 'store-and-network',
}

function AssignAgentsDialogDataContainer(props: DialogProps) {
  const [agentsTreeQueryRef, loadAgentsTreeQuery, disposeAgentsTreeQuery] =
    useQueryLoader<AssignAgentsDialogAssignablePoolItemsQuery>(assignableAgentPoolItemsQuery)
  React.useEffect(
    () =>
      subscribeOnOverallEvents(OVERALL_AGENT_POOL_EVENT_TOPICS, () =>
        loadAgentsTreeQuery(
          {agentPoolId: stringifyId(props.agentPoolId)},
          assignableAgentPoolItemsQueryFetchOptions,
        ),
      ),
    [loadAgentsTreeQuery, props.agentPoolId],
  )
  React.useEffect(() => disposeAgentsTreeQuery, [disposeAgentsTreeQuery])
  return agentsTreeQueryRef != null ? (
    <React.Suspense fallback={loader}>
      <AssignAgentsDialogContent queryRef={agentsTreeQueryRef} {...props} />
    </React.Suspense>
  ) : null
}

function AssignAgentsDialogContent({
  agentPoolId,
  onCloseAttempt,
  queryRef,
  agentsNumber,
  maxAgentsNumber,
}: ContentProps) {
  const {agentPool}: AssignAgentsDialogAssignablePoolItemsQuery$data = usePreloadedQuery(
    assignableAgentPoolItemsQuery,
    queryRef,
  )
  const stringAgentPoolId = stringifyId(agentPoolId)
  const [expandState, setExpandState, searchQuery, setSearchQuery] = useTreeState(emptyObject)
  const onSearchChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.currentTarget.value),
    [setSearchQuery],
  )
  const onSearchClear = React.useCallback(() => setSearchQuery(''), [setSearchQuery])

  const [
    cloudImagesSelection,
    selectedAgentTypeIds,
    toggleCloudImageSelection,
    setCloudImagesSelection,
  ] = useSelectionState()
  const [
    authorizedAgentsSelection,
    selectedAuthorizedAgentIds,
    toggleAuthorizedAgentSelection,
    setAuthorizedAgentsSelection,
  ] = useSelectionState()
  const [
    unauthorizedAgentsSelection,
    selectedUnauthorizedAgentIds,
    toggleUnauthorizedAgentSelection,
    setUnauthorizedAgentsSelection,
  ] = useSelectionState()

  const assignableAgentsTree = React.useMemo(
    () =>
      buildAssignableAgentsTree(agentPool, cloudImagesSelection, {
        ...authorizedAgentsSelection,
        ...unauthorizedAgentsSelection,
      }),
    [agentPool, authorizedAgentsSelection, cloudImagesSelection, unauthorizedAgentsSelection],
  )

  const matchingAgentsTree: AssignableAgentsTree = React.useMemo(
    () => matchAssignableAgentsTree(assignableAgentsTree, searchQuery),
    [assignableAgentsTree, searchQuery],
  )

  const setBranchItemsSelection = (branchRawId: string, selected: boolean) => {
    const branch = (selected ? matchingAgentsTree : assignableAgentsTree).find(
      ({rawId}) => rawId === branchRawId,
    )
    if (branch?.rawId === 'unauthorized') {
      setUnauthorizedAgentsSelection({
        ...unauthorizedAgentsSelection,
        ...branch?.items.reduce(
          (result, node) => ({...result, [node.rawId]: selected}),
          emptyObject,
        ),
      })
    } else {
      setCloudImagesSelection({
        ...cloudImagesSelection,
        ...branch?.items.reduce(
          (result, node) =>
            node.type === AgentTreeItemType.CLOUD_IMAGE && node.agentTypeRawId != null
              ? {...result, [node.agentTypeRawId]: selected}
              : result,
          emptyObject,
        ),
      })
      setAuthorizedAgentsSelection({
        ...authorizedAgentsSelection,
        ...branch?.items.reduce(
          (result, node) =>
            node.type === AgentTreeItemType.AGENT ? {...result, [node.rawId]: selected} : result,
          emptyObject,
        ),
      })
    }
  }

  const leftAgentSlotsCount =
    maxAgentsNumber != null && maxAgentsNumber !== -1 && agentsNumber != null
      ? maxAgentsNumber - agentsNumber
      : Infinity

  const {
    selectedCloudImagesCount,
    selectedCloudAgentsCount,
    selectedSelfHostedAgentsCount,
    selectedUnauthorizedAgentsCount,
  } = React.useMemo(() => getAgentsSelectionSummary(assignableAgentsTree), [assignableAgentsTree])

  return (
    <>
      <Search
        className={styles.searchInput}
        onChange={onSearchChange}
        onClear={onSearchClear}
        id={SEARCH_INPUT_ID}
        value={searchQuery}
        placeholder={'Search agents...'}
      />
      <div className={styles.tree}>
        {matchingAgentsTree.length === 0 ? (
          <span className={styles.emptyList}>{'Nothing to assign.'}</span>
        ) : null}
        {matchingAgentsTree.map(branch => {
          const agentPoolNode = assignableAgentsTree.find(({rawId}) => rawId === branch.rawId)
          const selectedItemsCount =
            (agentPoolNode?.selectedAgentsCount || 0) +
            (agentPoolNode?.selectedCloudImagesCount || 0)
          const checked = selectedItemsCount === agentPoolNode?.items.length
          const indeterminate = !checked && agentPoolNode != null && selectedItemsCount > 0

          return (
            <ExpandableGroup
              id={branch.rawId}
              key={branch.rawId}
              expanded={expandState[branch.rawId] !== false}
              onToggle={setExpandState}
              className={styles.expandableGroup}
              headerClassName={styles.expandableGroupHeader}
              headerProps={{
                onMouseEnter: onOverflowingElementMouseEnter,
                'data-overflow-title': branch.name,
              }}
              heading={
                branch.items.length === agentPoolNode?.items.length ? (
                  <Checkbox
                    onClick={stopPropagation}
                    checked={checked}
                    indeterminate={indeterminate}
                    onChange={() =>
                      setBranchItemsSelection(branch.rawId, !checked && !indeterminate)
                    }
                    containerClassName={styles.checkboxContainer}
                  >
                    <MatchingTitle searchQuery={searchQuery}>{branch.name}</MatchingTitle>
                  </Checkbox>
                ) : (
                  <MatchingTitle searchQuery={searchQuery}>{branch.name}</MatchingTitle>
                )
              }
              content={branch?.items.map(item => (
                <div key={`${item.type}-${item.rawId}`} className={styles.poolItem}>
                  <Checkbox
                    onChange={() =>
                      item.type === AgentTreeItemType.CLOUD_IMAGE && item.agentTypeRawId != null
                        ? toggleCloudImageSelection(item.agentTypeRawId)
                        : item.type === AgentTreeItemType.AGENT && item.authorized
                        ? toggleAuthorizedAgentSelection(item.rawId)
                        : toggleUnauthorizedAgentSelection(item.rawId)
                    }
                    checked={item.selected}
                    containerClassName={styles.itemCheckboxContainer}
                    cellClassName={styles.checkboxCell}
                    labelClassName={styles.checkboxLabel}
                  >
                    {item.type === AgentTreeItemType.CLOUD_IMAGE ? (
                      <div className={styles.itemTitle}>
                        <LinearAgentType
                          className={styles.itemName}
                          title={item.name || ''}
                          searchQuery={searchQuery}
                          onMouseEnter={onOverflowingElementMouseEnter}
                          data-overflow-title={item.name}
                        />
                        {item.instances != null ? (
                          <span className={styles.itemLabel}>{`(${
                            item.instances.count > 0 ? item.instances.count : 'no'
                          } ${plur('agent', item.instances.count)})`}</span>
                        ) : null}
                      </div>
                    ) : (
                      <div className={styles.itemTitle}>
                        <AgentItem
                          agentRef={item}
                          searchQuery={searchQuery}
                          onMouseEnter={onOverflowingElementMouseEnter}
                          data-overflow-title={item.name}
                          className={styles.itemName}
                        />
                        {item.connected === false ? (
                          <span className={styles.itemLabel}>{'Disconnected'}</span>
                        ) : item.enabled === false ? (
                          <span className={styles.itemLabel}>{'Disabled'}</span>
                        ) : null}
                      </div>
                    )}
                  </Checkbox>
                </div>
              ))}
            />
          )
        })}
      </div>
      <Panel className={styles.panel}>
        {agentPool?.name != null ? (
          <AgentsSelectionSummary
            className={styles.summary}
            targetAgentPoolName={agentPool.name}
            selectedCloudImagesCount={selectedCloudImagesCount}
            selectedCloudAgentsCount={selectedCloudAgentsCount}
            selectedSelfHostedAgentsCount={selectedSelfHostedAgentsCount}
            selectedUnauthorizedAgentsCount={selectedUnauthorizedAgentsCount}
            availableAgentSlotsCount={leftAgentSlotsCount}
          />
        ) : null}
        <AssignAgentsButton
          agentPoolRawId={stringAgentPoolId}
          agentTypeRawIds={selectedAgentTypeIds as Array<string>}
          authorizedAgentRawIds={selectedAuthorizedAgentIds as Array<string>}
          unauthorizedAgentRawIds={selectedUnauthorizedAgentIds as Array<string>}
          onAssign={onCloseAttempt}
          disabled={
            (selectedAuthorizedAgentIds.length === 0 &&
              selectedAgentTypeIds.length === 0 &&
              selectedUnauthorizedAgentIds.length === 0) ||
            selectedCloudAgentsCount + selectedSelfHostedAgentsCount > leftAgentSlotsCount
          }
        />
        <Button onClick={onCloseAttempt}>{'Cancel'}</Button>
      </Panel>
    </>
  )
}

export default React.memo(AssignAgentsDialogDataContainer)
